import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAccordion, MatDialog } from '@angular/material';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { combineLatest, merge, Observable, of, Subject } from 'rxjs';
import { map, shareReplay, skip, startWith, take, takeUntil } from 'rxjs/operators';
import { CognitoGroup } from 'src/API';
import { Assessment } from 'src/app/core/api/assessment.service';
import { StaffService } from 'src/app/core/api/staff.service';
import { AccessState } from 'src/app/core/schemas/subscription-plan.schema';
import { StaffPermissionsService } from 'src/app/core/services/staff-permissions.service';
import { SubscriptionPlanAccessService } from 'src/app/core/services/subscription-plan-access.service';
import { SignImageWizardModalComponent } from 'src/app/encounters/signs/sign-image-wizard/sign-image-wizard-modal/sign-image-wizard-modal.component';
import { ClinicSetupService } from 'src/app/logged-in-navbar/clinic-setup-modal/clinic-setup.service';
import { AssessmentRouteData } from '../../../encounters/assessment/assessment.component';
import { QrCodeModalComponent } from '../../qr-code-modal/qr-code-modal.component';
import { SymptomModes } from '../../symptoms/symptom.model';
import { Patient } from './../../../core/api/patient.service';
import { RealtimeMediaModalComponent } from './../realtime-media-modal/realtime-media-modal.component';
import { DryEyeAssessmentFormGroup } from './dry-eye-assessment-form/dry-eye-assessment-form.model';
import { DryEyeFormGroup } from './dry-eye-form/dry-eye-form.model';
import { DryEyeExpansionPanel, DryEyeFormsService } from './dry-eye-forms.service';
import {
  DryEyeMedicalHistoryFormGroup,
  DryEyeMedicalHistoryFormGroupValue,
  MedicalHistorySummarySnapshot
} from './dry-eye-medical-history-form/dry-eye-medical-history-form.model';
import { LinkQuestionnaireModalComponent } from './dry-eye-medical-history-form/link-assessment-to-questionnaire-modal/link-assessment-to-questionnaire-modal.component';
import { MedicalHistoryQuickScanComponent } from './dry-eye-medical-history-form/medical-history-quick-scan/medical-history-quick-scan.component';
import { MedicalHistoryRiskSummaryWithDetail } from './dry-eye-medical-history-form/medical-history-risk-factor.schema';
import { MedicalHistoryRiskSummaryService } from './dry-eye-medical-history-form/medical-history-risk-summary.service';
import { DryEyePredictionService } from './dry-eye-prediction.service';
import { PreOpService } from './pre-op/pre-op.service';
import { WeatherFormGroup } from './weather/weather-form.model';

// Stub used by medicalHistorySummary$ when no snapshot has been generated yet.
// Keeps the Observable's value type stable for template async pipes that read
// the badge counts and `hasAnyRisk`; both render as "no risk / hidden" when
// the snapshot is null.
const EMPTY_MEDICAL_HISTORY_SUMMARY: MedicalHistoryRiskSummaryWithDetail = {
  high: [],
  moderate: [],
  low: [],
  hasAnyRisk: false,
  clinicalContext: { primary: [], fitzpatrick: [] }
};

const EMPTY_MEDICAL_HISTORY_FORM_VALUE: DryEyeMedicalHistoryFormGroupValue = {};

// Every default value from DryEyeMedicalHistoryFormGroup's constructor that
// survives a null/undefined/empty-string/empty-array check. A control whose
// current value equals its entry here is "still at the default" and does not
// count as user-entered data. Keep in sync with the constructor in
// dry-eye-medical-history-form.model.ts.
const MEDICAL_HISTORY_DEFAULT_VALUES: { [key: string]: any } = {
  eyeItchiness: 0,
  eyeTearingQuantity: -10,
  symptomFrequency: 0,
  symptomSeverity: 0,
  medicalConditions: true,
  environmentalSymptoms: true,
  otherConditions: true
};

// Keys excluded from the data check entirely. `schemaVersion` is metadata
// stamped on by the child form's ngOnChanges. `medicalHistorySummaryGenerated`
// holds the saved AI summary snapshot — the button's other *ngIf guard already
// hides it when a snapshot exists, so we don't want this control's value to
// influence "has data".
const MEDICAL_HISTORY_IGNORED_KEYS: ReadonlySet<string> = new Set([
  'schemaVersion',
  'medicalHistorySummaryGenerated'
]);

function medicalHistoryFormHasUserData(value: { [key: string]: any } | null | undefined): boolean {
  if (!value) {
    return false;
  }
  for (const key of Object.keys(value)) {
    if (MEDICAL_HISTORY_IGNORED_KEYS.has(key)) {
      continue;
    }
    const controlValue = value[key];
    if (controlValue === null || controlValue === undefined || controlValue === '') {
      continue;
    }
    if (Array.isArray(controlValue) && controlValue.length === 0) {
      continue;
    }
    if (controlValue === MEDICAL_HISTORY_DEFAULT_VALUES[key]) {
      continue;
    }
    return true;
  }
  return false;
}

// Do not make this component OnPush or it will cause the follwing bug
// https://gitlab.com/clarity-lv/dry-eye-frontend/-/issues/325
@Component({
  selector: 'csi-dry-eye-forms',
  templateUrl: './dry-eye-forms.component.html',
  styleUrls: ['./dry-eye-forms.component.css']
})
export class DryEyeFormsComponent implements OnInit, OnDestroy {
  @ViewChild(MatAccordion, { static: false }) private accordion: MatAccordion;
  @ViewChild(MedicalHistoryQuickScanComponent, { static: false })
  public medicalHistoryQuickScan: MedicalHistoryQuickScanComponent;
  @Input() medicalHistoryFormGroup: DryEyeMedicalHistoryFormGroup;
  @Input() symptomsAndSignsFormGroup: DryEyeFormGroup;
  @Input() weatherFormGroup: WeatherFormGroup;
  @Input() assessmentFormGroup: DryEyeAssessmentFormGroup;
  @Input() expansionStateFormControl: FormControl;
  @Input() enabledStateFormControl: FormControl;
  @Input() patient: Patient;
  @Input() assignedDoctor: { firstName?: string; lastName?: string } | null = null;
  @Input() showImageRequiredError = false;
  @Input() assessmentRouteData$: Observable<AssessmentRouteData>;
  @Input() isFirstEncounter: boolean;
  @Input() assessment: Assessment;
  @Input() printMode: boolean;
  @Input() onPatientFileSaved$: Subject<Object>;
  @Input() saveFn: () => Observable<Assessment>;
  @Input() isLocked: boolean;

  public readonly SymptomModes = SymptomModes;
  public readonly ExpansionPanel = DryEyeExpansionPanel;
  public id: string;
  public showImpressionsWarning$: Observable<boolean>;
  private isUpdatingFormGroups = false;

  public assessmentMethod = null;
  public isNewAssessment = true;
  public questionnaires = [];
  public newlyLinkedQuestionnaires = [];
  onQuestionnaireLinked$: Subject<void> = new Subject<void>();
  // medicalHistorySummary$ / medicalHistoryFormValue$ are now DERIVED from the
  // saved snapshot stored on the medicalHistorySummaryGenerated form control —
  // they no longer recompute on every keystroke. Both streams emit only when
  // the saved snapshot changes (Generate / Retry sets a new one, or the form
  // is patched with a saved encounter on reload). Form edits to OTHER controls
  // do not touch these streams, so the AI Summary view stays frozen between
  // Generate / Retry presses.
  public medicalHistorySummary$: Observable<MedicalHistoryRiskSummaryWithDetail>;
  public medicalHistoryFormValue$: Observable<DryEyeMedicalHistoryFormGroupValue>;
  public showMedicalHistorySummary = false;
  // Mirrors the null/non-null state of the medicalHistorySummaryGenerated form
  // control so the template can switch between the Generate button and the
  // already-generated UI synchronously without going through async pipes.
  public medicalHistorySummaryGenerated = false;
  // Simple flag for the Generate button visibility — true if the patient has
  // typed text, picked a drug, or checked any condition box. Replaces the
  // previous live-stream `hasAnyRisk` check, which was running the entire risk
  // pipeline on every keystroke.
  public formHasMedicalHistoryData = false;
  public medicalHistoryRevealTrigger$ = new Subject<void>();
  private medicalHistoryDestroy$ = new Subject<void>();

  // ── Medical History header schemas ─────────────────────────────────────────
  // One source of truth for what the header renders. Template iterates these.

  public readonly medicalHistorySeverityOrder: ReadonlyArray<'high' | 'moderate' | 'low'> = [
    'high',
    'moderate',
    'low'
  ];

  public readonly medicalHistorySegments: ReadonlyArray<{
    key: 'form' | 'summary';
    icon: string;
    label: string;
    hasLiveDot: boolean;
  }> = [
    { key: 'form', icon: 'medical_history_form', label: 'Form', hasLiveDot: false },
    { key: 'summary', icon: 'medical_history_summary', label: 'AI Summary', hasLiveDot: true }
  ];

  public pendingScribeAttachment: any = null;
  public isDialogOpen = false;
  /** Captured after an auto-save on new assessments so the AI Scribe can extract immediately. */
  public savedAssessmentId: string | null = null;

  get clinicId(): string {
    return this.clinicSetupService.clinicId;
  }

  get aiScribeEnabledSections(): string[] {
    const setup = this.clinicSetupService.aiScribeSetup;
    if (!setup || !setup.sections) {
      return ['findings', 'patientProfile', 'history', 'symptoms', 'patientFile', 'assessment'];
    }
    return setup.sections;
  }

  get showAiScribe(): boolean {
    if (!this.assessmentMethod) {
      return false;
    }
    const schema = this.subscriptionPlanAccessService.flattenedSubscriptionPlanSchema;
    const featureEntry = schema['features.AI_SCRIBE'];
    const accessState = featureEntry && featureEntry.accessState;
    // Hidden for Lite (PRO_LOCKED) or explicitly HIDDEN in any plan
    if (accessState === AccessState.PRO_LOCKED || accessState === AccessState.HIDDEN) {
      return false;
    }
    // Pro/Full: respect developer override then clinic admin's toggle
    const setup = this.clinicSetupService.aiScribeSetup;
    if (!setup || setup.devEnabled === false) {
      return false;
    }
    return !!(setup && setup.enabled);
  }

  /** True when the button should be visible but shadowed/disabled (plan locked — upsell prompt) */
  get showAiScribeLocked(): boolean {
    const schema = this.subscriptionPlanAccessService.flattenedSubscriptionPlanSchema;
    const featureEntry = schema['features.AI_SCRIBE'];
    const accessState = featureEntry && featureEntry.accessState;
    if (accessState === AccessState.HIDDEN) {
      return false;
    }
    // Only show locked button for plan upgrade prompt — not when clinic admin has disabled it
    return accessState === AccessState.PRO_LOCKED;
  }

  public generateMedicalHistorySummary(): void {
    this.dryEyeFormsService.dryEyeFormsExpansionMap.medicalHistory = true;
    this.showMedicalHistorySummary = true;
    this.captureAndStoreMedicalHistorySnapshot();
  }

  public regenerateMedicalHistorySummary(): void {
    this.dryEyeFormsService.dryEyeFormsExpansionMap.medicalHistory = true;
    this.showMedicalHistorySummary = true;
    this.captureAndStoreMedicalHistorySnapshot();
  }

  // Defensively reshapes a saved snapshot's `summary` field so every array
  // the quick-scan template dereferences with `.length` is guaranteed to
  // exist. Without this, a snapshot whose backend round-trip dropped (say)
  // `clinicalContext.fitzpatrick` would land here with the field undefined,
  // pass the builder's short-circuit `||` check at line 258, and crash the
  // template at `viewModel.clinicalContextFitzpatrick.length`. Also handles
  // the legacy boolean values the field used to hold before this control was
  // repurposed to carry the snapshot bundle.
  private normalizeSavedMedicalHistorySummary(
    snapshot: MedicalHistorySummarySnapshot | null
  ): MedicalHistoryRiskSummaryWithDetail {
    if (!snapshot || !snapshot.summary || typeof snapshot.summary !== 'object') {
      return EMPTY_MEDICAL_HISTORY_SUMMARY;
    }
    const savedSummary = snapshot.summary as any;
    const savedClinicalContext = savedSummary.clinicalContext;
    return {
      high: Array.isArray(savedSummary.high) ? savedSummary.high : [],
      moderate: Array.isArray(savedSummary.moderate) ? savedSummary.moderate : [],
      low: Array.isArray(savedSummary.low) ? savedSummary.low : [],
      hasAnyRisk: !!savedSummary.hasAnyRisk,
      clinicalContext: {
        primary: Array.isArray(savedClinicalContext && savedClinicalContext.primary)
          ? savedClinicalContext.primary
          : [],
        fitzpatrick: Array.isArray(savedClinicalContext && savedClinicalContext.fitzpatrick)
          ? savedClinicalContext.fitzpatrick
          : []
      }
    };
  }

  // Runs the full risk-evaluation pipeline ONCE against the current form value,
  // bundles the result with a snapshot of the form value, writes the bundle to
  // the medicalHistorySummaryGenerated form control (which is the persisted
  // source of truth that the AI Summary view binds to), and fires the reveal
  // animation trigger. The service emits an immediate empty `startWith` value
  // before the medication-side-effects HTTP resolves, so we skip(1) and take(1)
  // to capture the real result.
  private captureAndStoreMedicalHistorySnapshot(): void {
    const currentFormValue = this.medicalHistoryFormGroup
      .value as DryEyeMedicalHistoryFormGroupValue;
    this.medicalHistorySummaryService
      .buildMedicalHistoryRiskSummaryWithDetail$(of(currentFormValue))
      .pipe(skip(1), take(1))
      .subscribe(computedSummary => {
        const snapshot: MedicalHistorySummarySnapshot = {
          summary: computedSummary,
          formValueSnapshot: currentFormValue
        };
        // Flip the boolean BEFORE setValue so handleSnapshotRestored no-ops via
        // its `!medicalHistorySummaryGenerated` guard. Without this, a first-
        // time Generate would run showRestoredContent (the load-from-saved
        // animation) on top of the reveal trigger; flipping first routes
        // Generate through the same animation path as Retry.
        this.medicalHistorySummaryGenerated = true;
        this.medicalHistoryFormGroup.controls.medicalHistorySummaryGenerated.setValue(snapshot);
        this.medicalHistoryRevealTrigger$.next();
      });
  }

  public adminAccessEnabled = this.staffService.isStaffPartOfGroup(
    this.staffService.staff || { cognitoGroups: null },
    CognitoGroup.Admin
  );

  constructor(
    private dryEyePredictionService: DryEyePredictionService,
    public dryEyeFormsService: DryEyeFormsService,
    public preOpService: PreOpService,
    private staffService: StaffService,
    public staffPermissionsService: StaffPermissionsService,
    private dialog: MatDialog,
    private router: Router,
    private translocoService: TranslocoService,
    private medicalHistorySummaryService: MedicalHistoryRiskSummaryService,
    private clinicSetupService: ClinicSetupService,
    private subscriptionPlanAccessService: SubscriptionPlanAccessService
  ) {}

  ngOnInit() {
    this.symptomsAndSignsFormGroup.controls.assessmentMethod.valueChanges.subscribe(
      assessmentMethod => {
        this.assessmentMethod = assessmentMethod;
      }
    );

    // this assumes form groups are set together at once and only once
    this.symptomsAndSignsFormGroup.assessmentMethod.subscribe(symptomMode => {
      this.medicalHistoryFormGroup.optional =
        symptomMode !== SymptomModes.Advanced && symptomMode !== SymptomModes.PreOp;
      this.updateFormGroups();
      this.showImpressionsWarning$ = this.areRecommendedFieldsValidInAdvancedMode(symptomMode);
    });

    this.dryEyePredictionService.medicalHistoryFormGroup = this.medicalHistoryFormGroup;
    this.dryEyePredictionService.symptomsAndSignsFormGroup = this.symptomsAndSignsFormGroup;
    this.dryEyePredictionService.patient = this.patient;

    this.questionnaires = this.filterQuestionnairesToThreeWeeksFromCompletion();

    // Single source of truth for the AI Summary VIEW: the persisted snapshot
    // on the medicalHistorySummaryGenerated form control. Emits when the user
    // presses Generate / Retry (which writes a new snapshot via setValue) or
    // when a saved encounter is patched into the form on reload. Form edits
    // to OTHER controls do not flow through this stream.
    const summarySnapshotControl = this.medicalHistoryFormGroup.controls
      .medicalHistorySummaryGenerated;
    const savedSnapshot$: Observable<MedicalHistorySummarySnapshot | null> = summarySnapshotControl.valueChanges.pipe(
      startWith(summarySnapshotControl.value),
      shareReplay(1)
    );

    this.medicalHistorySummary$ = savedSnapshot$.pipe(
      map(snapshot => this.normalizeSavedMedicalHistorySummary(snapshot)),
      shareReplay(1)
    );

    this.medicalHistoryFormValue$ = savedSnapshot$.pipe(
      map(snapshot =>
        snapshot && snapshot.formValueSnapshot
          ? snapshot.formValueSnapshot
          : EMPTY_MEDICAL_HISTORY_FORM_VALUE
      ),
      shareReplay(1)
    );

    // Generate-button visibility flag — true when the medical history form
    // contains any value beyond the constructor defaults (sliders at their
    // midpoint, section toggles still true, etc — see
    // MEDICAL_HISTORY_DEFAULT_VALUES). Works for both user input AND
    // programmatic patches (saved encounters reloaded by the resolver, linked
    // questionnaires), neither of which marks the form `dirty`/`touched`.
    merge(
      of(null),
      this.medicalHistoryFormGroup.valueChanges,
      this.medicalHistoryFormGroup.statusChanges
    )
      .pipe(
        map(() => medicalHistoryFormHasUserData(this.medicalHistoryFormGroup.value)),
        takeUntil(this.medicalHistoryDestroy$)
      )
      .subscribe(hasData => {
        this.formHasMedicalHistoryData = hasData;
      });

    // Restore handler — when the saved snapshot flips from null to non-null
    // (either via setValue from Generate / Retry or via a form patch on
    // reload), mirror the boolean flag and switch the UI to the AI Summary
    // tab. The setTimeout(0) waits for the quick-scan child to mount before
    // calling showRestoredContent on it.
    const handleSnapshotRestored = (snapshot: MedicalHistorySummarySnapshot | null): void => {
      if (snapshot && !this.medicalHistorySummaryGenerated) {
        this.medicalHistorySummaryGenerated = true;
        // Only switch to the summary tab for staff who can actually see it. The summary
        // pane and the toggle that switches back are both behind canViewClinicalNotes, so
        // flipping this for anyone else hides the form with no control left to restore it.
        const { canViewClinicalNotes } = this.staffPermissionsService.permissions;
        this.showMedicalHistorySummary = canViewClinicalNotes;
        setTimeout(() => {
          if (this.medicalHistoryQuickScan) {
            this.medicalHistoryQuickScan.showRestoredContent();
          }
        }, 0);
      }
    };

    summarySnapshotControl.valueChanges.subscribe(handleSnapshotRestored);

    // Synchronous check for the form-was-patched-BEFORE-ngOnInit case —
    // valueChanges only fires on changes, not initial values, so a saved
    // encounter loaded before ngOnInit would otherwise leave the boolean flag
    // stuck at false and the AI Summary view hidden despite saved data.
    if (summarySnapshotControl.value) {
      handleSnapshotRestored(summarySnapshotControl.value as MedicalHistorySummarySnapshot);
    }

    const pathname = location.pathname;
    this.id = pathname.substring(pathname.indexOf(':') + 1, pathname.length - 1);

    this.isNewAssessment = this.id === 'new';
  }

  ngOnDestroy() {
    this.dryEyeFormsService.dryEyeFormsExpansionMap = {};
    this.medicalHistoryRevealTrigger$.complete();
    this.medicalHistoryDestroy$.next();
    this.medicalHistoryDestroy$.complete();
  }

  openLinkQuestionnaireModal() {
    const dialogRef = this.dialog.open(LinkQuestionnaireModalComponent, {
      data: { saveFn: this.saveFn }
    });

    dialogRef.afterClosed().subscribe(newlyLinkedQuestionnaires => {
      this.onQuestionnaireLinked$.next(newlyLinkedQuestionnaires);
    });
  }

  openImageWizardModal() {
    this.saveNewAssessment().subscribe(data => {
      this.id = data.id;
      SignImageWizardModalComponent.open(
        this.dialog,
        this.symptomsAndSignsFormGroup,
        this.id,
        this.saveFn
      );
    });
  }

  openMobileImageWizardModal() {
    this.saveNewAssessment().subscribe(data => {
      this.id = data.id;
      const path = `${location.origin}/${this.id}/mobile-media-wizard`;
      QrCodeModalComponent.open(this.dialog, {
        qrCodePath: path,
        modalTitle: this.translocoService.translate('Scan to open Media Wizard on Mobile')
      });
    });
  }

  saveNewAssessment() {
    return this.id === 'new' ? this.saveFn() : of({ id: this.id });
  }

  filterQuestionnairesToThreeWeeksFromCompletion() {
    const threeWeeksInMilliSeconds = 1814400000;
    const assessmentDate = new Date();
    assessmentDate.setHours(0, 0, 0, 0);
    const assessmentTime = assessmentDate.getTime();

    const maxQuestionnaireTime = assessmentTime + threeWeeksInMilliSeconds;
    const minQuestionnaireTime = assessmentTime - threeWeeksInMilliSeconds;

    if (!this.patient) return [];
    return this.patient.questionnaireRequests.items.filter(questionnaire => {
      if (!questionnaire.completedAt) return false;
      const questionnaireDate = new Date(questionnaire.completedAt);
      questionnaireDate.setHours(0, 0, 0, 0);
      const questionnaireTime = questionnaireDate.getTime();
      return questionnaireTime <= maxQuestionnaireTime && questionnaireTime >= minQuestionnaireTime;
    });
  }

  private areRecommendedFieldsValidInAdvancedMode(
    assesmentMethod: SymptomModes
  ): Observable<boolean> {
    return combineLatest([
      merge(of(this.medicalHistoryFormGroup.status), this.medicalHistoryFormGroup.statusChanges),
      merge(of(this.symptomsAndSignsFormGroup.status), this.symptomsAndSignsFormGroup.statusChanges)
    ]).pipe(map(status => status.includes('INVALID') && assesmentMethod !== SymptomModes.Simple));
  }

  private updateFormGroups() {
    if (!this.isUpdatingFormGroups) {
      const controls = this.symptomsAndSignsFormGroup.controls;
      this.isUpdatingFormGroups = true;
      Object.keys(controls).forEach(key =>
        controls[key].updateValueAndValidity({ onlySelf: true })
      );
      this.symptomsAndSignsFormGroup.updateValueAndValidity();
      this.isUpdatingFormGroups = false;
    }
  }

  accordionExpansion() {
    this.accordion.openAll();
    Object.keys(this.dryEyeFormsService.dryEyeFormsExpansionMap).forEach(key => {
      this.dryEyeFormsService.dryEyeFormsExpansionMap[key] = true;
    });

    Object.keys(this.preOpService.expansionMap).forEach(key => {
      this.preOpService.expansionMap[key] = true;
    });
  }

  accordionCollapse() {
    this.accordion.closeAll();
    Object.keys(this.dryEyeFormsService.dryEyeFormsExpansionMap).forEach(key => {
      this.dryEyeFormsService.dryEyeFormsExpansionMap[key] = false;
    });

    Object.keys(this.preOpService.expansionMap).forEach(key => {
      this.preOpService.expansionMap[key] = false;
    });
  }

  goToMobileMediaWizard() {
    this.saveNewAssessment().subscribe(({ id }) => {
      this.router.navigateByUrl(`/${id}/mobile-media-wizard?return=true`);
    });
  }

  requestMediaFromApp() {
    this.saveNewAssessment().subscribe(data => {
      this.dialog.open(RealtimeMediaModalComponent, {
        data: {
          attachmentFormControl: this.assessmentFormGroup.controls.images
        }
      });
    });
  }
}
